import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Clock,
  Activity,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useAdminStats } from '../hooks';
import { useAuthStore } from '@/features/auth';
import { AdminQuickStats } from '../components';
import { GlassCard } from '@/components/glass';
import {
  ADMIN_COLORS,
} from '../utils/adminAnimations';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { AdminActivityBreakdownWidget } from '@/features/home/components/widgets/items/AdminActivityBreakdownWidget';
import { AdminRecentActivityWidget } from '@/features/home/components/widgets/items/AdminRecentActivityWidget';
import { AdminQuickActionsWidget } from '@/features/home/components/widgets/items/AdminQuickActionsWidget';

/* ── Skeleton ── */
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="p-4 rounded-xl animate-pulse"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <div
              className="h-3 rounded w-16 mb-3"
              style={{ backgroundColor: ADMIN_COLORS.border }}
            />
            <div
              className="h-8 rounded w-12"
              style={{ backgroundColor: ADMIN_COLORS.border }}
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="rounded-3xl p-5 animate-pulse"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <div
              className="h-4 rounded w-32 mb-4"
              style={{ backgroundColor: ADMIN_COLORS.border }}
            />
            <div className="space-y-3">
              {[0, 1, 2].map((j) => (
                <div
                  key={j}
                  className="h-10 rounded-xl"
                  style={{ backgroundColor: ADMIN_COLORS.ice }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth] = useState(currentMonth);

  const { data: displayStats, isLoading: isStatsLoading } = useAdminStats(selectedMonth);

  return (
    <motion.div
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4 } } }}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1
          className="text-lg sm:text-2xl font-semibold tracking-wide"
          style={{ color: ADMIN_COLORS.navy }}
        >
          {t('admin.welcome', { name: user?.firstName || '' })}
        </h1>
      </div>

      {/* Loading */}
      {isStatsLoading && <DashboardSkeleton />}

      {!isStatsLoading && displayStats && (
        <>
          {/* Stats Grid */}
          <AdminQuickStats stats={displayStats} />

          {/* Activity Overview (two columns) */}
          <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GlassCard title={t('admin.activityBreakdown')} icon={<Activity className="w-4 h-4" />}>
              <AdminActivityBreakdownWidget width={600} height={400} isEditMode={false} />
            </GlassCard>

            <GlassCard title={t('admin.recentActivity')} icon={<Clock className="w-4 h-4" />}>
              <AdminRecentActivityWidget width={600} height={400} isEditMode={false} />
            </GlassCard>
          </div>
          </ScrollReveal>

          {/* Recent Activity Feed */}
          <ScrollReveal delay={0.05}>
          <GlassCard
            title={t('admin.recentActivity')}
            headerAction={
              <Link
                to="/admin/activity"
                className="text-xs font-medium transition-colors"
                style={{ color: ADMIN_COLORS.primary }}
                onMouseEnter={(e) => (e.currentTarget.style.color = ADMIN_COLORS.primaryText)}
                onMouseLeave={(e) => (e.currentTarget.style.color = ADMIN_COLORS.primary)}
              >
                {t('admin.viewAll')}
              </Link>
            }
          >
            <AdminRecentActivityWidget width={600} height={400} isEditMode={false} />
          </GlassCard>
          </ScrollReveal>

          {/* Quick Actions */}
          <ScrollReveal delay={0.1}>
            <AdminQuickActionsWidget width={600} height={400} isEditMode={false} />
          </ScrollReveal>
        </>
      )}
    </motion.div>
  );
}
